import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useOnboardingSurvey } from '../hooks/useOnboardingSurvey';
import { useOnboardingFlow } from '../hooks/useOnboardingFlow';
import { finishInvitedOnboarding } from '../utils/finishInvitedOnboarding';
import { OnboardingLayout } from './OnboardingLayout';
import { OnboardingNavigation } from './OnboardingNavigation';
import { ProfileStep } from './ProfileStep';
import { SurveyStep } from './SurveyStep';
import { OrganizationStep } from './OrganizationStep';
import { analyticsService } from '@/shared/services/analytics';
import { logger } from '@/infrastructure/lib/logger';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';
import { AlertCircle } from 'lucide-react';

function OnboardingContainerContent() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { config: surveyConfig, isLoading: isSurveyLoading } = useOnboardingSurvey();
  const {
    currentStep,
    setCurrentStep,
    canSkipProfileStep,
    onboardingData,
    isLoading: isFlowLoading,
  } = useOnboardingFlow();

  // Convidado: já entrou com organização (via convite) — o onboarding termina
  // após perfil/survey, sem o step de criação de organização (bug 84095)
  const hasExistingOrganization = Boolean(onboardingData?.completion?.has_organization);
  
  const profileFormRef = useRef<HTMLFormElement>(null);
  const surveyFormRef = useRef<HTMLFormElement>(null);
  const organizationFormRef = useRef<HTMLFormElement>(null);
  
  // Estados de loading para cada step
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [isSurveySubmitting, setIsSurveySubmitting] = useState(false);
  const [isOrganizationSubmitting, setIsOrganizationSubmitting] = useState(false);
  const [isOrganizationFormComplete, setIsOrganizationFormComplete] = useState(false);
  
  // Se pode pular o profile step, já começa com ele marcado como completo
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(() => {
    return new Set();
  });

  // Marcar profile como completo se pode pular (dados do OAuth)
  useEffect(() => {
    if (canSkipProfileStep && !completedSteps.has('profile')) {
      setCompletedSteps(prev => new Set(prev).add('profile'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSkipProfileStep]);
  
  const hasSurvey = Boolean(surveyConfig && surveyConfig.fields && surveyConfig.fields.length > 0);

  // O step de survey também coleta o campo Função (obrigatório, persistido em
  // `position`) — mesmo sem survey dinâmica configurada, um convidado sem
  // `position` precisa passar por ele antes de concluir (review PR 13160).
  const hasPosition = Boolean(onboardingData?.profile?.position?.trim());
  const surveyStepRequired = hasSurvey || (hasExistingOrganization && !hasPosition);

  const markStepCompleted = useCallback((step: string) => {
    setCompletedSteps(prev => new Set(prev).add(step));
    
    // Track step completion
    const stepMap: Record<string, { name: string; number: number }> = {
      profile: { name: 'profile', number: 1 },
      survey: { name: 'survey', number: 2 },
      organization: { name: 'organization', number: 3 }
    };
    
    const stepInfo = stepMap[step];
    if (stepInfo) {
      analyticsService.trackEvent('onboarding_step_completed', {
        step_name: stepInfo.name,
        step_number: stepInfo.number
      });
    }
  }, []);
  
  const canNavigateToStep = useCallback((targetStep: string) => {
    // Se pode pular profile step, permitir navegação direta para survey/organization
    if (targetStep === 'profile') return !canSkipProfileStep; // Só pode voltar ao profile se NÃO pulou
    if (targetStep === 'survey') return completedSteps.has('profile') || canSkipProfileStep;
    if (targetStep === 'organization') {
      if (hasExistingOrganization) return false; // Convidado não cria organização
      if (hasSurvey) {
        return (completedSteps.has('profile') || canSkipProfileStep) && completedSteps.has('survey');
      }
      return completedSteps.has('profile') || canSkipProfileStep;
    }
    return false;
  }, [completedSteps, hasSurvey, canSkipProfileStep, hasExistingOrganization]);

  // Convidado sem nada a preencher (perfil pulado via OAuth, sem survey a
  // exibir e cargo já preenchido): conclui direto e vai para o dashboard.
  // Falha não é silenciosa: exibe painel de erro com retry (review PR 13160).
  const isAutoFinishPath =
    !isFlowLoading &&
    !isSurveyLoading &&
    hasExistingOrganization &&
    canSkipProfileStep &&
    !surveyStepRequired;
  const [autoFinishFailed, setAutoFinishFailed] = useState(false);
  const autoFinishTriggered = useRef(false);

  const runAutoFinish = useCallback(() => {
    autoFinishTriggered.current = true;
    setAutoFinishFailed(false);
    finishInvitedOnboarding().catch((err: unknown) => {
      autoFinishTriggered.current = false;
      setAutoFinishFailed(true);
      // Erro completo no log (não só a message) — preserva stack e metadados
      logger.error('[Onboarding] Auto-finish do onboarding de convidado falhou', {
        error: err,
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
    });
  }, []);

  useEffect(() => {
    if (!isAutoFinishPath || autoFinishFailed || autoFinishTriggered.current) return;
    runAutoFinish();
  }, [isAutoFinishPath, autoFinishFailed, runAutoFinish]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleBeforeUnload = () => {
        sessionStorage.removeItem('onboarding_profile');
        sessionStorage.removeItem('onboarding_survey');
        sessionStorage.removeItem('onboarding_organization');
        sessionStorage.removeItem('onboarding_avatar_preview');
        sessionStorage.removeItem('onboarding_logo_preview');
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, []);

  // Track onboarding started (apenas uma vez)
  const hasTrackedStart = useRef(false);
  useEffect(() => {
    if (!hasTrackedStart.current) {
      analyticsService.trackEvent('onboarding_started', {});
      hasTrackedStart.current = true;
    }
  }, []);

  // Callbacks para receber status de submitting dos steps
  const handleProfileSubmittingChange = useCallback((submitting: boolean) => {
    setIsProfileSubmitting(submitting);
  }, []);
  
  const handleSurveySubmittingChange = useCallback((submitting: boolean) => {
    setIsSurveySubmitting(submitting);
  }, []);
  
  const handleOrganizationSubmittingChange = useCallback((submitting: boolean) => {
    setIsOrganizationSubmitting(submitting);
  }, []);
  
  const handleOrganizationFormCompleteChange = useCallback((isComplete: boolean) => {
    setIsOrganizationFormComplete(isComplete);
  }, []);

  // Tela do auto-finish: spinner enquanto conclui; falha vira erro com retry
  if (isAutoFinishPath) {
    return (
      <OnboardingLayout>
        <div className="w-full max-w-md space-y-6 animate-fade-in">
          {autoFinishFailed ? (
            <>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("auth.onboarding.errors.title")}</AlertTitle>
                <AlertDescription>{t("auth.onboarding.autoFinish.errorDescription")}</AlertDescription>
              </Alert>
              <div className="flex justify-center">
                <Button onClick={runAutoFinish}>
                  {t("auth.onboarding.autoFinish.retryButton")}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner className="h-10 w-10" />
              <p className="text-sm text-muted-foreground">
                {t("auth.onboarding.autoFinish.finishing")}
              </p>
            </div>
          )}
        </div>
      </OnboardingLayout>
    );
  }

  // Renderizar step de perfil
  if (currentStep === 'profile') {
    const isLastStep = !surveyStepRequired && hasExistingOrganization;
    return (
      <>
        <OnboardingLayout>
          <ProfileStep
            setCurrentStep={setCurrentStep}
            hasSurvey={surveyStepRequired}
            userFirstName={user?.user_metadata?.first_name}
            userLastName={user?.user_metadata?.last_name}
            formRef={profileFormRef}
            onSubmittingChange={handleProfileSubmittingChange}
            onStepCompleted={() => markStepCompleted('profile')}
            finishAfterProfile={isLastStep}
          />
        </OnboardingLayout>

        <OnboardingNavigation
          currentStep={currentStep}
          hasSurvey={surveyStepRequired}
          canNavigateToStep={canNavigateToStep}
          onStepChange={setCurrentStep}
          onNext={() => profileFormRef.current?.requestSubmit()}
          isSubmitting={isProfileSubmitting}
          showOrganizationStep={!hasExistingOrganization}
          nextButtonLabel={isLastStep ? t("auth.onboarding.buttons.finish") : undefined}
        />
      </>
    );
  }

  // Renderizar step de survey
  if (currentStep === 'survey') {
    return (
      <>
        <OnboardingLayout>
          <SurveyStep
            setCurrentStep={setCurrentStep}
            surveyConfig={surveyConfig}
            formRef={surveyFormRef}
            onSubmittingChange={handleSurveySubmittingChange}
            onStepCompleted={() => markStepCompleted('survey')}
            finishAfterSurvey={hasExistingOrganization}
          />
        </OnboardingLayout>

        <OnboardingNavigation
          currentStep={currentStep}
          hasSurvey={surveyStepRequired}
          canNavigateToStep={canNavigateToStep}
          onStepChange={setCurrentStep}
          onBack={() => setCurrentStep('profile')}
          onNext={() => surveyFormRef.current?.requestSubmit()}
          isSubmitting={isSurveySubmitting}
          showBackButton={!canSkipProfileStep}
          showOrganizationStep={!hasExistingOrganization}
          nextButtonLabel={hasExistingOrganization ? t("auth.onboarding.buttons.finish") : undefined}
        />
      </>
    );
  }

  // Renderizar step de organização
  return (
    <>
      <OnboardingLayout>
        <OrganizationStep 
          formRef={organizationFormRef} 
          onSubmittingChange={handleOrganizationSubmittingChange}
          onFormCompleteChange={handleOrganizationFormCompleteChange}
        />
      </OnboardingLayout>
      
      <OnboardingNavigation
        currentStep={currentStep}
        hasSurvey={hasSurvey}
        canNavigateToStep={canNavigateToStep}
        onStepChange={setCurrentStep}
        onBack={() => {
          if (hasSurvey) {
            setCurrentStep('survey');
          } else if (!canSkipProfileStep) {
            setCurrentStep('profile');
          }
          // Se pulou profile e não tem survey, não tem para onde voltar
        }}
        onNext={() => organizationFormRef.current?.requestSubmit()}
        isSubmitting={isOrganizationSubmitting}
        showBackButton={hasSurvey || !canSkipProfileStep}
        nextButtonLabel={t("auth.onboarding.buttons.finish")}
        showFinishAnimation={isOrganizationFormComplete}
      />
    </>
  );
}

export default function OnboardingContainer() {
  return (
    <OnboardingContainerContent />
  );
}
